import { call, put, select } from "redux-saga/effects";

import { AgvanceAPI, SkyAPI } from "@ai360/core";

import { messages as globalMessages } from "~/i18n-messages";
import { getTheUserCompanyGuid, getUser } from "~/login";
import { getLastUsedLocationGuid, getTheUserPersonalityId } from "~/login/selectors";
import { actions as notificationActions, MSGTYPE } from "~/notifications";

import {
    isLinkedProductOnlyEnabled,
    toAgvanceLocationGuids,
    toAgvanceLocationIds,
    toLinkedProductGuids,
} from "./agvance-linked-products";

/**
 * Resolves the Agvance-linked product guids for a scope. Null means unrestricted (flag off, not
 * connected, no scope, or a failed fetch); [] means restricted with nothing matched, and toasts.
 */
export const fetchLinkedProductGuids = function* (
    customerGuid: string | null,
    errorActionType: string
): Generator<unknown, string[] | null, any> {
    const user = yield select(getUser);
    const personalityId = yield select(getTheUserPersonalityId);
    const companyGuid = yield select(getTheUserCompanyGuid);
    const lastUsedLocationGuid = yield select(getLastUsedLocationGuid);

    if (!isLinkedProductOnlyEnabled(user, personalityId)) {
        return null;
    }

    const resolvedOrgLevelGuid = lastUsedLocationGuid || companyGuid;

    const scopeKey = customerGuid ? { customerGuid } : { orgLevelGuid: resolvedOrgLevelGuid };
    if (!customerGuid && !resolvedOrgLevelGuid) {
        return null;
    }

    let locationsResult;
    let productsResult;
    try {
        locationsResult = yield put(
            SkyAPI.default.endpoints.getLocations.initiate(
                {
                    userLocationRestrictionType: SkyAPI.LocationRestrictionType.Product,
                    ...scopeKey,
                },
                { forceRefetch: true }
            )
        );
        const locations = yield call([locationsResult, locationsResult.unwrap]);
        const locationIds = toAgvanceLocationIds(locations);
        const locationGuids = toAgvanceLocationGuids(locations);

        if (locationIds.length === 0) {
            yield put(
                notificationActions.pushToasterMessage(
                    globalMessages.noLinkedAgvanceProducts,
                    MSGTYPE.INFO
                )
            );
            return [];
        }

        productsResult = yield put(
            AgvanceAPI.default.endpoints.getLinkedProductList.initiate(
                { locationIds, locationGuids, ...scopeKey },
                { forceRefetch: true }
            )
        );
        const products = yield call([productsResult, productsResult.unwrap]);
        const linkedProductGuids = toLinkedProductGuids(products);

        if (linkedProductGuids.length === 0) {
            yield put(
                notificationActions.pushToasterMessage(
                    globalMessages.noLinkedAgvanceProducts,
                    MSGTYPE.INFO
                )
            );
        }

        return linkedProductGuids;
    } catch (error) {
        yield put(notificationActions.apiCallError(error, { type: errorActionType }));
        return null;
    } finally {
        if (locationsResult && locationsResult.unsubscribe) {
            locationsResult.unsubscribe();
        }
        if (productsResult && productsResult.unsubscribe) {
            productsResult.unsubscribe();
        }
    }
};
